import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getDisplayMessage } from "@/api/apiClient";
import { processFileWithAI } from "@/lib/extractWithRetry";
import { Upload, FileText, Loader2, CheckCircle, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import { safeValorForInput, safeDataForInput, safeStringForInput } from "@/utils";

function formatarTempo(segundos) {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ContratoUploadIA({ onSalvar, editais, isLoading }) {
  const [arquivo, setArquivo] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [tempoDecorrido, setTempoDecorrido] = useState(0);
  const [dadosExtraidos, setDadosExtraidos] = useState(null);
  const [etapa, setEtapa] = useState("upload");
  const timerRef = useRef(null);
  const inicioRef = useRef(null);

  useEffect(() => {
    if (!processando) return;
    inicioRef.current = Date.now();
    setTempoDecorrido(0);
    timerRef.current = setInterval(() => {
      setTempoDecorrido(Math.floor((Date.now() - inicioRef.current) / 1000));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [processando]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Extração por IA suporta apenas PDF. Use um arquivo PDF ou preencha manualmente.");
      e.target.value = "";
      return;
    }

    if (file.size > 52428800) {
      toast.error("Arquivo muito grande! Máximo 50MB.");
      e.target.value = "";
      return;
    }

    setArquivo(file);
    setDadosExtraidos(null);
    setEtapa("upload");
  };

  const processarArquivo = async () => {
    if (!arquivo) return;

    setProcessando(true);
    setEtapa("processando");

    try {
      toast.info("Enviando arquivo...");

      const promptExtracao = `Você é um especialista em análise de contratos administrativos brasileiros.

TAREFA: Extraia TODOS os dados solicitados do contrato anexado, seguindo RIGOROSAMENTE as regras abaixo.

═══════════════════════════════════════════════════════════════
REGRAS CRÍTICAS DE EXTRAÇÃO - SEGUIR SEM EXCEÇÃO:
═══════════════════════════════════════════════════════════════

1. COPIE NA ÍNTEGRA - Nunca resuma, nunca parafraseie. Extraia o texto exato quando aplicável.
2. BUSCA EXAUSTIVA - Para cada campo, busque em TODO o documento: capa, preâmbulo, cláusulas, anexos, aditivos.
3. NÚMERO DO CONTRATO: Busque por "CONTRATO Nº", "Nº DO CONTRATO", "Contrato n°", "Número do contrato". Geralmente na primeira página ou cabeçalho.
4. NÚMERO DO PROCESSO: Busque por "Processo nº", "Nº do processo", "Processo Licitatório", "Pregão nº", "PE nº".
5. ÓRGÃO CONTRATANTE: Busque no início do documento, por "CONTRATANTE", "CONCEDENTE", "ÓRGÃO", "ADMINISTRAÇÃO PÚBLICA", "MUNICÍPIO DE", "ESTADO DE", "UNIÃO".
6. FORNECEDOR: Busque por "CONTRATADA", "CONTRATADO", "FORNECEDOR", "EMPRESA", "LICITANTE VENCEDORA", "RAZÃO SOCIAL".
7. VALOR TOTAL (CRÍTICO): Está quase sempre na PRIMEIRA PÁGINA ou em cláusula de vigência. Busque por "VALOR TOTAL", "VALOR GLOBAL", "valor da contratação", "valor do contrato", "R$", tabelas de valores, cronograma físico-financeiro, planilha de custos. Retorne APENAS o número (ex: 150000.50). Se houver múltiplos valores, use o valor total/global do contrato.
8. DATAS (OBRIGATÓRIO - data_inicio e data_fim): Contratos SEMPRE têm vigência. Busque na PRIMEIRA PÁGINA, cláusulas de vigência, cronograma, assinaturas e rodapé por: "vigência", "vigência de", "período de", "início em", "término em", "data de início", "data de término", "assinado em", "a partir de", "até", "de ___ a ___", "entre ___ e ___". Se encontrar DD/MM/AAAA ou "dia de mês de ano", CONVERTA para YYYY-MM-DD. NUNCA deixe vazio se houver qualquer data no contrato.
9. OBJETO: Busque por "OBJETO", "DO OBJETO", "especificação", descrição do contrato. Extraia o texto completo.
10. TIPO DE CONTRATO: Busque por "modalidade", "tipo de contrato", "contrato de", "Pregão", "Concorrência", "Dispensa", "Inexigibilidade".

CAMPOS PRIORITÁRIOS (busque OBRIGATORIAMENTE - NUNCA deixe vazio se existir no documento):
- numero_contrato, numero_processo, orgao_contratante, fornecedor, valor_total, data_inicio, data_fim, objeto, tipo_contrato

DATAS: Contratos SEMPRE têm vigência. Procure em cláusulas de vigência, cronograma, assinaturas, rodapé. Formato final: YYYY-MM-DD.

Retorne um objeto JSON com TODOS os campos solicitados. Use os nomes das chaves EXATAMENTE como listados (snake_case).`;

      const schema = {
        type: "object",
        properties: {
          numero_contrato: {
            type: "string",
            description: "Número do contrato. Busque por CONTRATO Nº, Nº DO CONTRATO. Ex: 001/2024, 123/2025",
          },
          numero_processo: {
            type: "string",
            description: "Número do processo licitatório. Busque por Processo nº, Pregão nº, PE nº",
          },
          modalidade: { type: "string", description: "Modalidade: Pregão, Concorrência, Dispensa, Inexigibilidade, etc." },
          tipo_contrato: {
            type: "string",
            description: "Tipo: Serviços, Obras, Compras, Locação, etc. Busque em 'contrato de' ou 'objeto'",
          },
          orgao_contratante: {
            type: "string",
            description: "Nome completo do órgão/entidade contratante. Busque CONTRATANTE, CONCEDENTE",
          },
          unidade_gestora: { type: "string", description: "Unidade gestora ou setor responsável" },
          fornecedor: {
            type: "string",
            description: "Razão social da empresa contratada. Busque CONTRATADA, FORNECEDOR",
          },
          cnpj_cpf: { type: "string", description: "CNPJ ou CPF do fornecedor" },
          gestor_contrato: { type: "string", description: "Nome do gestor do contrato" },
          responsavel_interno: { type: "string", description: "Responsável interno pela fiscalização" },
          objeto: {
            type: "string",
            description: "Objeto/descrição do contrato. Busque OBJETO, DO OBJETO. Extraia texto completo",
          },
          valor_total: {
            type: "number",
            description: "CRÍTICO - Valor total em reais. Busque na primeira página, cláusula de vigência, cronograma ou tabelas. APENAS número (ex: 10323971.52). 'R$ 1.500.000,00' → 1500000. NUNCA retorne null se houver valor no documento.",
          },
          valor_mensal_previsto: { type: "number", description: "Valor mensal previsto em reais" },
          data_inicio: {
            type: "string",
            description: "CRÍTICO - Data de início da vigência. Busque em cláusula de vigência, cronograma, assinaturas. Retorne APENAS YYYY-MM-DD. '30/01/2026' → 2026-01-30. 'Janeiro de 2026' → 2026-01-01. NUNCA retorne null se houver data no documento.",
          },
          data_fim: {
            type: "string",
            description: "CRÍTICO - Data de término da vigência. Busque em cláusula de vigência, cronograma. Retorne APENAS YYYY-MM-DD. '12/12/2026' → 2026-12-12. NUNCA retorne null se houver data no documento.",
          },
          periodicidade_reajuste: { type: "string", description: "Periodicidade do reajuste" },
          forma_pagamento: { type: "string", description: "Forma de pagamento" },
          centro_custo: { type: "string", description: "Centro de custo" },
          fonte_recurso: { type: "string", description: "Fonte de recurso" },
          classificacao_orcamentaria: { type: "string", description: "Classificação orçamentária" },
          multas_penalidades: { type: "string", description: "Cláusulas de multas e penalidades" },
          clausulas_principais: { type: "string", description: "Principais cláusulas do contrato" },
        },
      };

      toast.info("Analisando documento com IA...");

      // Usa mesma lógica de Processar Edital: retry no upload, retry na extração, OCR se vazio
      const { file_url, output: rawOutput, result: resultado } = await processFileWithAI(arquivo, {
        prompt: promptExtracao,
        schema,
        keyFieldsForEmptyCheck: [
          "numero_contrato",
          "orgao_contratante",
          "fornecedor",
          "valor_total",
          "objeto",
        ],
        criticalFieldsForOcrRetry: ["valor_total", "data_inicio", "data_fim"],
        document_type: "contrato",
        ocr_page_limit: 15,
        tryOcrIfEmpty: true,
        onOcrFallback: () => toast.info("Tentando com OCR (PDF escaneado)..."),
      });

      let output = rawOutput;
      if (!output || typeof output !== 'object') {
        throw new Error('A IA não retornou dados estruturados. Tente novamente.');
      }

      // Normalizar chaves: IA pode retornar variações (camelCase, português, etc.)
      const keyMap = {
        numeroContrato: 'numero_contrato',
        numeroProcesso: 'numero_processo',
        orgaoContratante: 'orgao_contratante',
        tipoContrato: 'tipo_contrato',
        valorTotal: 'valor_total',
        dataInicio: 'data_inicio',
        dataFim: 'data_fim',
      };
      output = { ...output };
      Object.entries(keyMap).forEach(([alt, canonical]) => {
        if (output[alt] != null && (output[canonical] == null || output[canonical] === '')) {
          output[canonical] = output[alt];
        }
      });

      // Normalizar datas para YYYY-MM-DD (input type="date" exige esse formato)
      output.data_inicio = safeDataForInput(output.data_inicio, false) || '';
      output.data_fim = safeDataForInput(output.data_fim, true) || '';

      // Normalizar valor_total: usa mesma lógica de safeValorForInput
      const valorNorm = safeValorForInput(output.valor_total);
      output.valor_total = valorNorm !== '' ? valorNorm : (typeof output.valor_total === 'number' ? output.valor_total : '');
      const valorMensalNorm = safeValorForInput(output.valor_mensal_previsto);
      if (valorMensalNorm !== '') output.valor_mensal_previsto = valorMensalNorm;

      // Normalizar strings: IA pode retornar objeto em vez de string → evita "[object Object]"
      const strFields = ['orgao_contratante', 'fornecedor', 'numero_contrato', 'numero_processo', 'objeto', 'tipo_contrato', 'cnpj_cpf'];
      strFields.forEach((f) => {
        const norm = safeStringForInput(output[f]);
        if (norm !== '' || output[f] != null) output[f] = norm;
      });

      // Resposta quando LLM não está configurado (retorna { text: '', raw: '' })
      const isLLMNaoConfigurado =
        (output.text === '' && output.raw === '' && Object.keys(output).length <= 2) ||
        (resultado?.message && String(resultado.message).toLowerCase().includes('não configurado'));

      if (isLLMNaoConfigurado) {
        toast.error(
          resultado?.message || 'IA não configurada. O administrador deve configurar em Super Admin > Config. IA Editais.'
        );
        setEtapa('upload');
        return;
      }

      const camposChave = ['numero_contrato', 'orgao_contratante', 'fornecedor', 'valor_total', 'objeto'];
      const todosVazios = camposChave.every(
        (c) => output[c] == null || String(output[c]).trim() === ''
      );

      if (todosVazios) {
        toast.error(
          'A IA não identificou dados no documento. Verifique: (1) PDF com texto selecionável ou imagem nítida; (2) IA configurada no Super Admin; (3) Tente outro arquivo ou preencha manualmente.'
        );
        setEtapa('upload');
        return;
      }

      toast.success('Dados extraídos! Revise e edite antes de salvar.');

      setDadosExtraidos({
        ...output,
        arquivo_contrato_url: file_url,
        arquivo_contrato_nome: arquivo.name,
        dados_extraidos_ia: output,
      });

      setEtapa('validacao');
    } catch (error) {
      console.error("Erro ao processar arquivo:", error);
      const msg = (error?.message || error?.data?.message || "").toLowerCase();
      const isPdfTooLarge = msg.includes("pdf muito grande") || (msg.includes("máximo") && msg.includes("páginas"));
      const isPdfEscaneado = msg.includes("extrair texto") || msg.includes("não foi possível") || msg.includes("texto extraível");
      toast.error(
        isPdfTooLarge
          ? "O PDF excede o limite de 500 páginas. Envie um arquivo com no máximo 500 páginas ou divida o documento."
          : isPdfEscaneado
            ? "O documento parece ser uma imagem escaneada. A extração automática pode falhar. Tente um PDF com texto selecionável ou preencha manualmente."
            : getDisplayMessage(error, "Erro ao processar arquivo. Tente novamente.")
      );
      setEtapa("upload");
    } finally {
      setProcessando(false);
    }
  };

  const handleConfirmar = () => {
    if (!dadosExtraidos) return;

    const isValidIsoDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);

    const valorNorm = safeValorForInput(dadosExtraidos.valor_total);
    const dataInicioNorm = safeDataForInput(dadosExtraidos.data_inicio);
    const dataFimNorm = safeDataForInput(dadosExtraidos.data_fim, true);

    // Evita enviar payload com datas inválidas (ex.: '' ou outro formato),
    // que pode causar erro na conversao backend/Prisma e deixar a UI presa.
    const data_inicio =
      dataInicioNorm && isValidIsoDate(dataInicioNorm) ? dataInicioNorm : null;
    const data_fim =
      dataFimNorm && isValidIsoDate(dataFimNorm) ? dataFimNorm : null;

    if (data_inicio === null || data_fim === null) {
      toast.error("Datas inválidas ao salvar. Revise data de início e data de fim.");
      return;
    }

    // Mesmo documento do upload: arquivo principal + registro em Documentos adicionais (aba Documentos)
    const docUrl = dadosExtraidos.arquivo_contrato_url;
    const docNome =
      dadosExtraidos.arquivo_contrato_nome || (arquivo && arquivo.name) || "Documento enviado";
    let anexos = Array.isArray(dadosExtraidos.anexos) ? [...dadosExtraidos.anexos] : [];
    if (docUrl && !anexos.some((a) => a && a.url === docUrl)) {
      anexos.push({
        url: docUrl,
        nome: docNome,
        categoria: "Documento original (IA)",
        data_adicao: new Date().toISOString(),
      });
    }

    const payload = {
      ...dadosExtraidos,
      // Importante: backend/Prisma espera null/Decimal/Date ('' pode quebrar conversões)
      valor_total: valorNorm !== '' ? valorNorm : (typeof dadosExtraidos.valor_total === 'number' ? dadosExtraidos.valor_total : null),
      data_inicio,
      data_fim,
      validado_ia: true,
      anexos,
    };
    onSalvar(payload);
  };

  const handleEditar = (campo, valor) => {
    setDadosExtraidos((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  return (
    <div className="space-y-6">
      {etapa === "upload" && (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900 mb-2">
              Upload do Contrato
            </p>
            <p className="text-sm text-slate-600 mb-4">
              PDF (até 50MB)
            </p>
            <Input
              type="file"
              onChange={(e) => handleFileChange(e)}
              accept=".pdf"
              className="hidden"
              id="file-upload-contrato"
            />
            <Button
              type="button"
              onClick={() => document.getElementById("file-upload-contrato")?.click()}
              variant="outline"
            >
              Selecionar Arquivo
            </Button>
          </div>

          {arquivo && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">{arquivo.name}</p>
                    <p className="text-sm text-green-700">
                      {(arquivo.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  onClick={processarArquivo}
                  disabled={processando}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Processar com IA
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {etapa === "processando" && (
        <div className="py-12 space-y-6">
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Processando Contrato...
            </h3>
            <p className="text-slate-600 mb-6">
              A IA está analisando o documento.
            </p>
          </div>
          <div className="max-w-md mx-auto space-y-3">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div className="h-full w-1/3 bg-blue-600 rounded-full animate-progress-loading" />
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-600">
              <Clock className="w-4 h-4" />
              <span className="font-mono font-medium">{formatarTempo(tempoDecorrido)}</span>
            </div>
          </div>
        </div>
      )}

      {etapa === "validacao" && dadosExtraidos && (
        <div className="space-y-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">
                  Dados Extraídos pela IA
                </h3>
                <p className="text-sm text-blue-700">
                  Revise e edite antes de salvar.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Número do Contrato</Label>
              <Input
                value={safeStringForInput(dadosExtraidos.numero_contrato)}
                onChange={(e) => handleEditar("numero_contrato", e.target.value)}
              />
            </div>
            <div>
              <Label>Número do Processo</Label>
              <Input
                value={safeStringForInput(dadosExtraidos.numero_processo)}
                onChange={(e) => handleEditar("numero_processo", e.target.value)}
              />
            </div>
            <div>
              <Label>Órgão Contratante</Label>
              <Input
                value={safeStringForInput(dadosExtraidos.orgao_contratante)}
                onChange={(e) => handleEditar("orgao_contratante", e.target.value)}
              />
            </div>
            <div>
              <Label>Fornecedor</Label>
              <Input
                value={safeStringForInput(dadosExtraidos.fornecedor)}
                onChange={(e) => handleEditar("fornecedor", e.target.value)}
              />
            </div>
            <div>
              <Label>Valor Total (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={safeValorForInput(dadosExtraidos.valor_total)}
                onChange={(e) => {
                  const v = e.target.value;
                  handleEditar("valor_total", v === "" ? "" : (parseFloat(v) || ""));
                }}
              />
            </div>
            <div>
              <Label>Data de Início</Label>
              <Input
                type="date"
                value={safeDataForInput(dadosExtraidos.data_inicio)}
                onChange={(e) => handleEditar("data_inicio", e.target.value)}
              />
            </div>
            <div>
              <Label>Data de Fim</Label>
              <Input
                type="date"
                value={safeDataForInput(dadosExtraidos.data_fim, true)}
                onChange={(e) => handleEditar("data_fim", e.target.value)}
              />
            </div>
            <div>
              <Label>Tipo de Contrato</Label>
              <Input
                value={safeStringForInput(dadosExtraidos.tipo_contrato)}
                onChange={(e) => handleEditar("tipo_contrato", e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEtapa("upload");
                setDadosExtraidos(null);
                setArquivo(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmar}
              disabled={isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar e Salvar
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}